/**
 * Resets scroll and focus when the route changes.
 *
 * React Router does not do this. A full page load lands at the top, but a
 * client-side navigation only swaps the tree — the scroll offset is left exactly
 * where it was. So clicking a link in the footer of a long page loads the new
 * page and leaves you at the bottom of it, staring at another footer, having to
 * scroll up to find out where you are.
 *
 * Four cases, and the reason each is handled differently:
 *
 *   1. **A new path.** Scroll to the top. This is the bug.
 *
 *   2. **A path with a `#hash`.** Scroll to that element instead. The FAQ page's
 *      contents rail is built from in-page anchors, so a blanket jump-to-top
 *      would break the one navigation that is supposed to move you *down* the
 *      page.
 *
 *   3. **Back and forward.** Do nothing. Browsers restore the previous offset
 *      for history entries themselves, and that is the behaviour people expect —
 *      going back should return you to the link you clicked, not to the top of
 *      a page you already read. Router reports these as `POP`.
 *
 *   4. **Query-string-only changes.** Do nothing. Switching a tool tab updates
 *      the URL without changing the page, and yanking the viewport to the top
 *      mid-task would be worse than the original bug.
 *
 * ## Focus, not just scroll
 *
 * The same defect exists for anyone not using a mouse, and it is invisible in a
 * screenshot: after a route change, keyboard focus stays on the link that was
 * clicked — which no longer exists — so it falls back to `<body>`, and a screen
 * reader announces nothing at all about having arrived somewhere new. Moving
 * focus to the `<main>` landmark makes the new page announce itself and puts the
 * next Tab press at the start of the content instead of back at the top of the
 * site. `preventScroll` is essential here: focusing an element scrolls it into
 * view by default, which would fight the positioning done just above.
 */

import { useEffect } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { sendPageView } from '@/lib/ga'
import { sendAhrefsPageView } from '@/lib/ahrefs'

export const RouteChange = () => {
  const { pathname, hash } = useLocation()
  const navigationType = useNavigationType()

  /*
   * Analytics page views live here because this is the only place that knows a
   * navigation happened. GA4's automatic `page_view` is switched off in the
   * `<head>` install (`send_page_view: false`, set in `scripts/gaHead.ts`):
   * without that, the SPA's soft navigations — every route change after the
   * first — would never be reported at all, since gtag.js only knows about the
   * URL it was loaded on. This effect is what reports them, including into
   * `/resume-builder`.
   *
   * Separate effect from the scroll handling below: this one must also run for
   * `POP`, because pressing Back is a page view even though it must not reset
   * the scroll position.
   */
  useEffect(() => {
    sendPageView(pathname, typeof document !== 'undefined' ? document.title : undefined)
    // Ahrefs re-checks consent itself and does nothing until the answer is Allow.
    sendAhrefsPageView()
  }, [pathname])

  /*
   * Re-reports the current page the moment consent is granted — **added
   * 2026-09-19**. A first-time visitor's initial `page_view` above fires
   * before they have answered `ConsentBanner`, so on production it goes out
   * under `analytics_storage: denied` (a cookieless, modelled ping GA4 does
   * not surface in Realtime). Clicking "Allow" updates the Consent Mode
   * signal (`src/lib/ads.ts`'s `writeConsent`) but that alone does not
   * re-send anything — confirmed live: accepting consent produced zero new
   * network requests until a route change. Without this, a visitor who
   * accepts consent on the page they landed on is only ever tracked from
   * their *next* navigation, not the page they are actually looking at.
   *
   * This calls the exact same `sendPageView` as every other navigation, for
   * the path already open — not a new/custom event. It can fire at most once
   * per page, because `ats:consent` is only ever dispatched by an actual
   * Accept/Decline click, and `ConsentBanner` unmounts itself after
   * answering (it never answers twice for the same visitor).
   */
  useEffect(() => {
    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent<'granted' | 'denied'>).detail
      if (detail !== 'granted') return
      sendPageView(pathname, typeof document !== 'undefined' ? document.title : undefined)
      sendAhrefsPageView()
    }
    window.addEventListener('ats:consent', onConsent)
    return () => window.removeEventListener('ats:consent', onConsent)
  }, [pathname])

  useEffect(() => {
    // Case 3: let the browser restore its own history position.
    if (navigationType === 'POP') return

    if (hash) {
      const target = document.querySelector(hash)
      if (target) {
        target.scrollIntoView()
        return
      }
      // A hash pointing at nothing should still land somewhere sensible.
    }

    /*
     * `instant`, explicitly. `html { scroll-behavior: smooth }` is set globally
     * for in-page anchors, and without this override a route change would
     * animate the whole way up — turning an instant navigation into a scroll
     * the user has to sit through.
     */
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

    const main = document.getElementById('main')
    if (main) {
      main.focus({ preventScroll: true })
    }
    // `pathname` only: a query-string change must not re-trigger this (case 4).
  }, [pathname, hash, navigationType])

  return null
}
